/**
 * Mistik Dream · Fichas: a mesa vista de cima.
 *
 * <p>Cada personagem é um <b>card</b> com o essencial da sessão — nome, retrato e as
 * barras de HP e SP —, e a ficha inteira mora atrás do clique. É o recorte que uma mesa
 * pede: durante a sessão o que se olha o tempo todo é quanto de vida cada um tem, e
 * abrir seis fichas completas lado a lado seria uma tela que ninguém lê.
 *
 * <p><b>A cor do card é a da conta de quem joga</b>, a mesma do resto do GG Pilot. Isso
 * dá de graça o que uma mesa precisa: reconhecer de quem é o personagem antes de ler o
 * nome dele.
 *
 * <p><b>Quem cria é o dono; quem escreve depois é o mestre.</b> A regra é da mesa e mora
 * no servidor — a tela só a espelha. Por isso a distribuição dos 36 pontos acontece na
 * CRIAÇÃO ({@link ./CriarPersonagem}): é o único momento em que a ficha é do jogador.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErroApi } from '../../api/cliente';
import { useSessao } from '../../auth/SessaoContext';
import { TituloDaPagina } from '../../shell/TituloDaPagina';
import { CriarPersonagem } from './CriarPersonagem';
import { FichaCompleta } from './FichaCompleta';
import { fracao, interpretarFicha, nivelDoPersonagem } from './fichaDeMistikDream';
import type { Ficha } from './fichaDeMistikDream';
import * as fichasApi from './fichasApi';
import './fichas.css';

export function Fichas() {
  const { usuario } = useSessao();
  const souMestre = Boolean(usuario?.mestre);
  const clienteDeConsultas = useQueryClient();

  const [aberta, setAberta] = useState<number | null>(null);
  const [criando, setCriando] = useState(false);
  const [erro, setErro] = useState('');

  const lista = useQuery({
    queryKey: fichasApi.CHAVE_DAS_FICHAS,
    queryFn: fichasApi.listar,
    staleTime: Infinity,
  });

  const criacao = useMutation({
    mutationFn: (novo: { nome: string; conteudo: Ficha }) => fichasApi.criar(novo),
    onSuccess: () => {
      setCriando(false);
      setErro('');
      void clienteDeConsultas.invalidateQueries({ queryKey: fichasApi.CHAVE_DAS_FICHAS });
    },
    onError: (e) => setErro(e instanceof ErroApi ? e.message : 'Não consegui criar a ficha.'),
  });

  const exclusao = useMutation({
    mutationFn: fichasApi.excluir,
    onSuccess: () => {
      setAberta(null);
      void clienteDeConsultas.invalidateQueries({ queryKey: fichasApi.CHAVE_DAS_FICHAS });
    },
    onError: (e) => setErro(e instanceof ErroApi ? e.message : 'Não consegui apagar a ficha.'),
  });

  const fichaAberta = lista.data?.find((f) => f.id === aberta);

  return (
    <div className="fi-md">
      <TituloDaPagina>
        As fichas da mesa de Mistik Dream. Clique num personagem para abrir a ficha
        inteira.
      </TituloDaPagina>

      <p className="fi-md-estado">
        {souMestre ? (
          <>
            Você mestra esta mesa: escreve em <b>todas</b> as fichas, e é de você que sai o
            XP das perícias.
          </>
        ) : (
          <>
            Cada um cria a própria ficha e distribui os pontos de atributo na criação.
            Depois disso, quem escreve nela é o <b>mestre</b>.
          </>
        )}
      </p>

      {erro && <p className="fi-md-erro">{erro}</p>}

      {lista.isLoading && <p className="fi-md-espera">Abrindo a mesa…</p>}

      {lista.error && (
        <p className="fi-md-erro">
          {lista.error instanceof Error ? lista.error.message : 'Não foi possível abrir.'}
        </p>
      )}

      {lista.data?.length === 0 && (
        <p className="fi-md-vazio">
          Nenhum personagem na mesa ainda. Crie o seu — é o que cada jogador faz uma vez.
        </p>
      )}

      <div className="fi-md-mesa">
        {lista.data?.map((f) => (
          <CardDePersonagem key={f.id} ficha={f} aoAbrir={() => setAberta(f.id)} />
        ))}

        <button type="button" className="fi-md-card mais" onClick={() => setCriando(true)}>
          <span>+</span>
          Criar meu personagem
        </button>
      </div>

      {criando && (
        <CriarPersonagem
          criando={criacao.isPending}
          erro={erro}
          aoFechar={() => setCriando(false)}
          aoCriar={(nome, conteudo) => criacao.mutate({ nome, conteudo })}
        />
      )}

      {fichaAberta && (
        <FichaCompleta
          ficha={fichaAberta}
          souMestre={souMestre}
          aoFechar={() => setAberta(null)}
          aoGravar={() =>
            void clienteDeConsultas.invalidateQueries({ queryKey: fichasApi.CHAVE_DAS_FICHAS })
          }
          aoExcluir={() => {
            if (window.confirm(`Apagar a ficha de ${fichaAberta.nome}? Isto não volta.`)) {
              exclusao.mutate(fichaAberta.id);
            }
          }}
        />
      )}
    </div>
  );
}

/**
 * O card: nome, nível, retrato e as duas barras que a mesa consulta o tempo todo.
 *
 * <p>Turnos saíram da ficha — são do grupo, e vivem no Registro. O XP fica fora do card
 * de propósito: muda devagar, e um card com três barras deixa de ser um relance e vira
 * uma ficha pequena, que é justamente o que o clique existe para abrir.
 */
function CardDePersonagem({
  ficha,
  aoAbrir,
}: {
  ficha: fichasApi.Ficha;
  aoAbrir: () => void;
}) {
  const f = interpretarFicha(ficha.conteudo);
  const foto = ficha.imagem || ficha.avatarDoDono;

  return (
    <button
      type="button"
      className="fi-md-card"
      style={{ borderColor: ficha.corDoDono }}
      onClick={aoAbrir}
      title={`Abrir a ficha de ${ficha.nome}`}
    >
      {foto ? (
        <img className="fi-md-retrato" src={foto} alt="" />
      ) : (
        <span className="fi-md-retrato iniciais" style={{ background: ficha.corDoDono }}>
          {ficha.nome.trim().charAt(0).toUpperCase() || '?'}
        </span>
      )}

      <span className="fi-md-nome">{ficha.nome}</span>
      <span className="fi-md-dono" style={{ color: ficha.corDoDono }}>
        {ficha.nomeDoDono} · nível {nivelDoPersonagem(f.xp)}
      </span>

      <span className="fi-md-vital hp">
        <b>HP</b>
        <span className="fi-md-trilho">
          <i style={{ width: `${fracao(f.hp) * 100}%`, background: '#b0544d' }} />
        </span>
        <span className="fi-md-numero">
          {f.hp.atual}/{f.hp.max}
        </span>
      </span>

      <span className="fi-md-vital sp">
        <b>SP</b>
        <span className="fi-md-trilho">
          <i style={{ width: `${fracao(f.sp) * 100}%`, background: '#5b8fb9' }} />
        </span>
        <span className="fi-md-numero">
          {f.sp.atual}/{f.sp.max}
        </span>
      </span>
    </button>
  );
}
